<template>
  <ElInput
    :id="id"
    ref="elInput"
    :model-value="displayValue"
    :disabled="disabled"
    :placeholder="placeholder"
    inputmode="decimal"
  />
</template>

<script lang="ts" setup>
import AutoNumeric from 'autonumeric';
import { ElInput } from 'element-plus';
import { onMounted, onUnmounted, ref, watch } from 'vue';

interface Props {
  modelValue: string | number | null,
  asString?: boolean,
  placeholder?: string,
  disabled?: boolean,
  id?: string,
}

const props = withDefaults(defineProps<Props>(), {
  placeholder: undefined,
  id: undefined,
});

const emit = defineEmits<{
  'update:modelValue': [value: string | number | null]
}>();

const elInput = ref<InstanceType<typeof ElInput>>();

const displayValue = ref('');

let autoNumeric: AutoNumeric | null = null;

function toNumber(value: string | number | null): number | null {
  if (value === null || value === '') return null;

  const parsed = Number(value);

  return Number.isNaN(parsed) ? null : parsed;
}

function currentValue(): number | null {
  if (elInput.value?.input?.value === '') return null;

  return autoNumeric?.getNumber() ?? null;
}

function syncDisplayValue(): void {
  displayValue.value = elInput.value?.input?.value ?? '';
}

function onInputCapture(event: Event): void {
  event.stopImmediatePropagation();
  syncDisplayValue();
}

function onRawValueModified(): void {
  syncDisplayValue();

  const value = currentValue();

  if (value === toNumber(props.modelValue)) return;

  emit('update:modelValue', value !== null && props.asString ? `${value}` : value);
}

onMounted(() => {
  const input = elInput.value?.input;

  if (!input) {
    throw new Error('Input ref is undefined');
  }

  autoNumeric = new AutoNumeric(input, toNumber(props.modelValue), {
    ...AutoNumeric.getPredefinedOptions().euro,
    decimalPlaces: 2,
    emptyInputBehavior: 'null',
    modifyValueOnWheel: false,
    unformatOnHover: false,
    watchExternalChanges: true,
  });

  syncDisplayValue();

  input.addEventListener('autoNumeric:rawValueModified', onRawValueModified);
  input.addEventListener('input', onInputCapture, true);
});

watch(() => props.modelValue, (nextValue) => {
  const value = toNumber(nextValue);

  if (autoNumeric === null || value === currentValue()) return;

  autoNumeric.set(value);
  syncDisplayValue();
});

onUnmounted(() => {
  elInput.value?.input?.removeEventListener('autoNumeric:rawValueModified', onRawValueModified);
  elInput.value?.input?.removeEventListener('input', onInputCapture, true);
  autoNumeric?.remove();
  autoNumeric = null;
});
</script>
